import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "convex/react";
import SuperLayoutTitle from "@/components/layout/super-layout/super-layout-title";
import { api } from "@/convex/_generated/api";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Building, Users, TrendingUp, ArrowRight } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_super/super/control-panel/")({
  component: ControlPanelIndex,
});

function ControlPanelIndex() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const companies = useQuery(api.companies.queries.getCompanies, {});

  const filteredCompanies = companies?.filter((company: any) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      company.name.toLowerCase().includes(search) ||
      company.legalName?.toLowerCase().includes(search) ||
      company.email?.toLowerCase().includes(search)
    );
  });

  const handleCompanyClick = (companyId: string) => {
    navigate({
      to: "/super/control-panel/$companyId/overview",
      params: { companyId },
    });
  };

  return (
    <>
      <SuperLayoutTitle title="Panel de Control" />
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar empresa por nombre, legal, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {!companies ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded" />
                    <div className="h-3 bg-muted rounded w-5/6" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredCompanies && filteredCompanies.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No se encontraron empresas
              </h3>
              <p className="text-sm text-muted-foreground">
                {searchTerm
                  ? "Intenta con otros términos de búsqueda"
                  : "No hay empresas registradas"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredCompanies?.map((company: any) => (
              <Card
                key={company._id}
                className={cn(
                  "cursor-pointer transition-all hover:shadow-md hover:border-primary/50",
                  company.status === "inactive" && "opacity-60",
                )}
                onClick={() => handleCompanyClick(company._id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{company.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {company.legalName || company.email}
                      </CardDescription>
                    </div>
                    <Badge
                      variant={
                        company.status === "active" ? "default" : "secondary"
                      }
                    >
                      {company.status === "active" ? "Activa" : "Inactiva"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building className="h-4 w-4" />
                      <span className="truncate">
                        {company.email || "Sin email"}
                      </span>
                    </div>

                    {company.phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{company.phone}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingUp className="h-4 w-4" />
                      <span>
                        Creada{" "}
                        {format(company._creationTime, "PP", { locale: es })}
                      </span>
                    </div>

                    <div className="pt-3 flex justify-end">
                      <Button variant="ghost" size="sm" className="gap-2">
                        Ver detalles
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
